
import {Component, Prop, Vue} from "vue-property-decorator";
import DataFieldBar from "./base/DataFieldBar.vue";
import OkDialog from "../../common/OkDialog.vue";
import DataFieldCache from "../../../models/data.field.cache";
import {dataFieldsEditModule} from "../../../store";
import {stringify} from "@/auxillary/datafield.stringify";
import Hints from "@/components/study/datafields/base/Hints.vue";
import {DateTime} from "luxon";
import {formatDate} from "@/auxillary/date-format";

const UnknownPart = "UK";
const EmptyPart2 = "XX";

@Component({
  name: 'MedicalDateField',
  components: {
    DataFieldBar,
    OkDialog,
    Hints
  },
})
export default class MedicalDateField extends Vue {

  @Prop({required: true})
  private dataField: DataFieldCache

  private dayValid: boolean = true;
  private monthValid: boolean = true;
  private yearValid: boolean = true;
  private dateValid: boolean = true;
  private maxDateValid: boolean = true;

  errorMessage: string = "";
  errorMessages = {
    requiredValue: "Bitte angeben.",
    invalidEntry: "Ungültige Eingabe.",
    invalidDate: "Ungültiges Datum.",
    invalidFormatDay: "Format DD erwartet.",
    invalidFormatMonth: "Format MM erwartet.",
    invalidFormatYear: "Format YYYY erwartet.",
    min: "Minimum: ",
    max: "Maximum: ",
    valueDeleted: "Wert wird gelöscht."
  }
  menu = false;

  get min() {
    const min = (this.field.definition?.min as any);
    return this.formatDateString(min);
  }

  get max() {
    const max = (this.field.definition?.max as any);
    return this.formatDateString(max);
  }

  get maxAllowedEntryDate() {
    const maxAllowedEntryDate = (this.field.definition?.maxAllowedEntryDate as any);
    return this.formatDateString(maxAllowedEntryDate);
  }

  formatDateString(dateString: string) {
    if (dateString == "today") {
      const now = DateTime.now();
      return now.toFormat("yyyy-MM-dd");
    }
    return dateString;
  }

  get minimalMax() {
    if (this.max == undefined)
      return this.maxAllowedEntryDate;

    if (this.maxAllowedEntryDate == undefined)
      return this.max;

    const maxAllowedEntryDate = DateTime.fromISO(this.maxAllowedEntryDate).toMillis();
    const max = DateTime.fromISO(this.max).toMillis();
    if (max < maxAllowedEntryDate)
      return this.max;
    return this.maxAllowedEntryDate;
  }

  get pickerMax() {
    return this.minimalMax;
  }

  get day() {
    return this.dataField.values?.day ?? ""
  }

  set day(value: string) {
    if (value == null) value = "";
    dataFieldsEditModule.setValue({field: this.dataField, key: "day", value})
  }

  get needsDay() {
    return this.dataField.definition.needsDay
  }

  isDayValid(value: any) {
    if (!this.unknown && !this.ongoing) {
      if (this.isRequired && this.needsDay && !value) {
        this.dayValid = false;
        return this.errorMessages.requiredValue;
      }

      if (!!value && value != UnknownPart && value.length != 2) {
        this.dayValid = false;
        return this.errorMessages.invalidFormatDay;
      }

      if (!!value && (
          (isNaN(parseInt(value)) && (value != UnknownPart || !this.canDayBeUnknown)) ||
          (!isNaN(parseInt(value)) && (value <= 0 || value >= 32)))) {
        this.dayValid = false;
        return this.errorMessages.invalidEntry;
      }
      this.dayValid = true;
      this.isMaxValid();
    } else {
      this.dayValid = true;
    }

    return true;
  }

  isMaxValid() {

    if (!this.dayValid || !this.monthValid || !this.yearValid ||
        !this.year || this.year == "" || (!this.max && !this.maxAllowedEntryDate)) {
      return;
    }

    const year = this.year;
    const month = this.month == "" ? "01" : this.month;
    const day = this.day == "" ? "01" : this.day;
    const date = DateTime.fromISO(year + "-" + month + "-" + day);
    if (!date.isValid) {
      this.errorMessage = "Ungültiges Datum";
      return;
    } else this.errorMessage = "";
    if (this.maxAllowedEntryDate != undefined) {
      const maxDate = DateTime.fromISO(this.maxAllowedEntryDate);
      if (maxDate.toMillis() >= date.toMillis()) {
        this.maxDateValid = true;
        this.errorMessage = "";
      } else {
        this.maxDateValid = false;
        this.errorMessage = this.errorMessages.max + formatDate(this.maxAllowedEntryDate) + ". " + this.errorMessages.valueDeleted;
        this.day = "";
        this.month = "";
        this.year = "";
        return;
      }
    }

    if (this.max != undefined) {
      const maxDate = DateTime.fromISO(this.max);
      if (maxDate.toMillis() >= date.toMillis()) {
        this.maxDateValid = true;
        this.errorMessage = "";
      } else {
        this.maxDateValid = false;
        this.errorMessage = this.errorMessages.max + formatDate(this.max);
        return;
      }
    }
  }

  get month() {
    return this.dataField.values?.month ?? "";
  }

  set month(value: string) {
    if (value == null) value = "";

    if (value == UnknownPart) this.day = UnknownPart;
    dataFieldsEditModule.setValue({field: this.dataField, key: "month", value})
  }

  get needsMonth() {
    return (this.dataField.definition?.needsMonth as any);
  }

  isMonthValid(value: any) {
    if (!this.unknown && !this.ongoing) {
      if (this.isRequired && this.needsMonth && !value && !this.unknown && !this.ongoing) {
        this.monthValid = false;
        return this.errorMessages.requiredValue;
      }

      if (!!value && value.length != 2) {
        this.monthValid = false;
        return this.errorMessages.invalidFormatMonth;
      }

      if (!!value && (
          (isNaN(parseInt(value)) && (value != UnknownPart || !this.canMonthBeUnknown)) ||
          (!isNaN(parseInt(value)) && (value <= 0 || value >= 13)))) {
        this.monthValid = false;
        return this.errorMessages.invalidEntry;
      }
      this.monthValid = true;
      this.isMaxValid();
    } else {
      this.monthValid = true;
    }

    return true;
  }

  get year() {
    return this.dataField.values?.year ?? ""
  }

  set year(value: string) {
    if (value == null) value = "";
    dataFieldsEditModule.setValue({field: this.dataField, key: "year", value})
    this.updateValue()
  }

  isYearValid(value: any) {
    if (!this.unknown && !this.ongoing) {
      if (this.isRequired && !value) {
        this.yearValid = false;
        return this.errorMessages.requiredValue;
      }

      if (!!value && value.length != 4) {
        this.yearValid = false;
        return this.errorMessages.invalidFormatYear;
      }

      if (!!value && (isNaN(parseInt(value)) || (!isNaN(parseInt(value)) && value <= 0))) {
        this.yearValid = false;
        return this.errorMessages.invalidEntry;
      }
      this.yearValid = true;
      this.isMaxValid();
    } else {
      this.yearValid = true;
    }

    return true;
  }

  get unknown() {
    return this.dataField.values?.unknown
  }

  set unknown(value: boolean) {
    if (value) {
      this.day = "";
      this.month = "";
      this.year = "";
      this.ongoing = false;
    }
    dataFieldsEditModule.setValue({field: this.dataField, key: "unknown", value})
    this.updateValue();
  }

  get ongoing() {
    return this.dataField.values?.ongoing
  }

  set ongoing(value: boolean) {
    if (value) {
      this.day = ""
      this.month = ""
      this.year = ""
      this.unknown = false;
    }
    dataFieldsEditModule.setValue({field: this.dataField, key: "ongoing", value})
    this.updateValue();
  }

  get datePicker() {
    const date = new Date(this.dataField?.values?.value);
    if (date instanceof Date && !isNaN(date.getTime()))
      return this.dataField?.values?.value
    return ""
  }

  set datePicker(value: string) {
    const date: Date = new Date(value);
    this.day = date.getDate().toString().padStart(2, "0");
    this.month = (date.getMonth() + 1).toString().padStart(2, "0");
    this.year = date.getFullYear().toString();

    this.dayValid = true;
    this.monthValid = true;
    this.yearValid = true;

    this.updateValue();
  }

  get isVisible() {
    return this.field.isVisible;
  }

  get isReadonly() {
    return !this.field.isEditable;
  }

  get dateDisabled() {
    return this.ongoingDisabled || this.unknownDisabled;
  }

  get ongoingDisabled() {
    return this.unknown ?? false;
  }

  get unknownDisabled() {
    return this.ongoing ?? false;
  }

  get displayName() {
    if (!this.dataField.displayName) return ""
    return this.dataField.displayName + (this.isRequired ? "<span style='color:red;'>*</span>" : "");
  }

  stringifyValue() {
    return stringify(this.field)
  }

  get isRequired() {
    return this.dataField.isRequired;
  }

  get canBeUnknown() {
    return (this.field.definition?.canBeUnknown as any) ?? false;
  }

  get canMonthBeUnknown() {
    return (this.field.definition?.canMonthBeUnknown as any) ?? false;
  }

  get canDayBeUnknown() {
    return (this.field.definition?.canDayBeUnknown as any) ?? false;
  }

  get canBeOngoing() {
    return (this.field.definition?.canBeOngoing as any) ?? false;
  }

  get field() {
    return this.dataField;
  }

  updateValue() {
    let value = "";

    if (this.unknown && this.canBeUnknown) {
      value = "unknown";
    } else if (this.ongoing && this.canBeOngoing) {
      value = "ongoing";
    } else {
      if (this.year == "") {
        value = "";
      } else if (this.month == "") {
        value = `${this.year}-${EmptyPart2}-${EmptyPart2}`;
      } else if (this.day == "") {
        value = `${this.year}-${this.month}-${EmptyPart2}`;
      } else {
        value = `${this.year}-${this.month}-${this.day}`;
      }
    }

    dataFieldsEditModule.setValue({field: this.dataField, key: "value", value: value})
    this.dateValid = this.validateDate();
  }

  validateDate() {
    if (!this.ongoing && !this.unknown && this.dayValid && this.monthValid && this.yearValid) {
      // additional validation checks
      // currently for debugging
      /*const dateLimit = new Date("2021-01-01")
      const date = new Date(this.value);
      console.log(this.value, date, dateLimit)
      if (date instanceof Date && !isNaN(date.getTime()) && date < dateLimit) {
        this.errorMessage = "Date needs to be after date limit.";
        this.dateValid = false;
        return false;
      }*/
    }
    //this.errorMessage = "";
    return true;
  }

  validateKeyPress(evt: KeyboardEvent) {
    const keysAllowed: string[] = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'];
    const keyPressed: string = evt.key;
    if (!keysAllowed.includes(keyPressed)) {
      evt.preventDefault()
    }
  }

}
